import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ActivityItem } from '@atlassian/recent-work-client/query';

import { type NLPSearchWorkResultRecentActivityDataType } from '../ai-answer-dialog/types';

const MAX_CONTRIBUTORS = 3;
export const sanitizeAndMapRecentWorkActivity: (
	recentWork: ActivityItem[],
) => NLPSearchWorkResultRecentActivityDataType[] = (recentWork: ActivityItem[]) => {
	const uniqRecents = new Set();

	return recentWork.reduce<NLPSearchWorkResultRecentActivityDataType[]>(
		(result, recentWorkItem) => {
			const containerName =
				recentWorkItem.containers.length > 0
					? recentWorkItem.containers[0].name
					: recentWorkItem.object.product;
			// creating a key with the container name and title to
			// prevent the posibilty of multiple items with the same name
			const key = `${containerName}:${recentWorkItem.object.name || ''}`;
			if (uniqRecents.has(key)) {
				return result;
			}

			uniqRecents.add(key);
			const curRecentWorkItem = {
				title: recentWorkItem.object.name || '',
				url: recentWorkItem.object.url,
				containerName,
				event: recentWorkItem.eventType as NLPSearchWorkResultRecentActivityDataType['event'],
				timestamp: recentWorkItem.timestamp,
				iconUrl: !!recentWorkItem.object.icon ? recentWorkItem.object.icon.value : '',
				contributors: recentWorkItem.contributors.slice(0, 3).map((contributor) => ({
					name: contributor.name,
					id: contributor.accountId,
				})),
				othersCount:
					recentWorkItem.contributors.length > MAX_CONTRIBUTORS
						? recentWorkItem.contributors.length - MAX_CONTRIBUTORS
						: 0,
				contentTypeName: recentWorkItem.object.__typename,
			};

			return result.concat(curRecentWorkItem);
		},
		[],
	);
};

type FormatTimeKnowledgeCardArgs = {
	locale: string;
	zoneInfo?: string | null;
};

/**
 * Formats the time for a person knowledge card based on locale of the viewing user and zoneInfo for the surfaced users.
 **/
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const formatTimeKnowledgeCard: ({
	locale,
	zoneInfo,
}: FormatTimeKnowledgeCardArgs) => string | undefined = ({
	locale,
	zoneInfo,
}: FormatTimeKnowledgeCardArgs) => {
	return zoneInfo
		? new Date()
				.toLocaleTimeString(locale, {
					timeZone: zoneInfo,
					hour: 'numeric',
					minute: 'numeric',
				})
				.replace(' ', '')
				.toLowerCase()
		: undefined;
};

const ATLAS_PROD_URL = 'https://home.atlassian.com';
const ATLAS_STAGING_URL = 'https://home.stg.atlassian.com';

const getAtlasHost = () => {
	const host = window.location.host;
	const isStaging =
		host.includes('localhost') ||
		host.includes('.stg.atlassian') ||
		host.includes('.stg-east.frontend.public.atl-paas.net') ||
		host.includes('jira-dev.com');

	return isStaging ? ATLAS_STAGING_URL : ATLAS_PROD_URL;
};

const getAtlasURL = (orgId: string) => {
	const atlasHost = getAtlasHost();
	return orgId ? `${atlasHost}/o/${orgId}` : atlasHost;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getAgentProfileUrl: (agent: Agent, orgId: string) => string = (
	agent: Agent,
	orgId: string,
) => {
	const atlasURL = getAtlasURL(orgId);

	return `${atlasURL}/people/agent/${agent.id}`;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getTeamProfileUrl: (teamId: string, orgId: string) => string = (
	teamId: string,
	orgId: string,
) => {
	const atlasURL = getAtlasURL(orgId);
	return `${atlasURL}/people/team/${teamId}`;
};
